import { z } from 'zod';
import { PACING_INTERVAL_MAX_MS, PACING_INTERVAL_MIN_MS, EVAL_RUN_DEADLINE_MIN_MS, EVAL_RUN_DEADLINE_MAX_MS, EVAL_PACING_PRESET_NAMES, EVAL_MAX_RUNS_PER_SCENARIO } from '../eval-pacing';
import { evalDateContextSchema } from '../eval-variables';
import { MAX_ATTACHMENTS_PER_MESSAGE, timestampSchema, uuidSchema } from './common';
import { EVAL_ATTACHMENT_HASH_REGEX } from './storage-keys';

// Re-export so `@wayai/core/contracts` consumers (web + MCP) can name the preset
// union without re-spelling its members.
export type { EvalPacingPreset } from '../eval-pacing';

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

const evalIdParam = z.object({
  id: z.string().uuid(),
});

const sessionIdParam = z.object({
  id: z.string().uuid(),
});

const scenarioSetIdParam = z.object({
  id: z.string().uuid(),
});

const hubIdQuery = z.object({
  hub_id: z.string().uuid(),
});

// Declared in `eval-variables` with its constructor and guard; that module says why.
export { evalDateContextSchema };
export type { EvalDateContext } from '../eval-variables';

export const EVAL_FIXTURE_NAME_MAX_LENGTH = 200;

/** A non-blank Rekor fixture selected for one session launch. */
export const evalFixtureOverrideSchema = z.string().trim().min(1).max(EVAL_FIXTURE_NAME_MAX_LENGTH);

/**
 * Starting an eval session may replace the suite's declared fixture for this one
 * launch. The suite still supplies the target base; this is deliberately one
 * scalar, not a fixture matrix or a second authoring shape (gh #3626).
 */
export const runSessionQuery = hubIdQuery.extend({
  fixture: evalFixtureOverrideSchema.optional(),
});

/**
 * `POST /journeys/:id/run`. `call_mode=true` launches the journey's session in call mode
 * (`sessionConfigSchema.call_mode`): each step's turn runs as a call turn would.
 */
export const runJourneyQuery = hubIdQuery.extend({
  call_mode: z.enum(['true', 'false']).optional(),
});

// ---------------------------------------------------------------------------
// Eval input (the turn trigger)
// ---------------------------------------------------------------------------

/** Roles an eval's `input` (the turn trigger) may declare. See `evalInputRoleError`. */
export const EVAL_INPUT_ROLES = ['user', 'system'] as const;

/** Derived, so adding a role can't leave the rejection message lying. */
const EVAL_INPUT_ROLE_LIST = EVAL_INPUT_ROLES.map((r) => `"${r}"`).join(' or ');

/** Longest received role echoed back before truncation. */
const ROLE_ECHO_MAX = 40;

/**
 * Bounded, non-throwing rendering of a received role.
 *
 * `JSON.stringify` is NOT safe here: it recurses, and `JSON.parse` accepts
 * nesting depths it cannot re-serialize — so a body the request parser happily
 * accepted would throw `RangeError` while being described, turning malformed
 * input into a 500. That is the class this rule exists to close, so the
 * rejection path must not reintroduce it. A non-string role only needs its
 * type named; only a string is worth echoing, and only up to a bound.
 */
function describeRole(role: unknown): string {
  if (typeof role === 'string') {
    return JSON.stringify(role.length > ROLE_ECHO_MAX ? `${role.slice(0, ROLE_ECHO_MAX)}…` : role);
  }
  if (role === null) return 'null';
  if (Array.isArray(role)) return 'an array';
  if (typeof role === 'object') return 'an object';
  return `a ${typeof role}`;
}

/**
 * Coerce an eval `input` to the record the fire path will read: the stored
 * column may hold an object OR a raw JSON string, and `resolveRunInputMessage`
 * parses either. Callers that rule on `input` must accept both shapes —
 * an object-only check is bypassable by stringifying the body (gh #3371).
 * A non-object (or unparseable string) is malformed, and not this rule's
 * business to report.
 */
export function asEvalInputRecord(input: unknown): Record<string, unknown> | null {
  const value = parseMaybeJson(input);
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/** A stored JSON column's value in either shape: parsed, or the raw string the TEXT column tolerates. */
function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return null; }
}

/**
 * `null` when an eval `input` declares a deliverable role, else the rejection.
 *
 * An eval's `input` is the trigger the responder answers, and only `user` and
 * `system` describe something answerable (gh #3371):
 *
 *   - `tool` cannot be honored at all. The schedule row carries no
 *     `tool_call_id`, and the producer collapses every role it doesn't
 *     recognize to `sender_type: 'system'` — a sender the history mapper drops
 *     — so a tool result reaches the turn, if at all, as an internal nudge
 *     stripped of the call it was answering.
 *   - `assistant` IS representable (it rides `sender_type: 'agent'` and reaches
 *     the LLM as an assistant turn) but it is not a *trigger*: it asks the
 *     responder to answer its own utterance, leaving `expected` compared
 *     against a reply to nothing.
 *
 * So the write paths refuse both rather than accept a trigger they cannot act
 * on: the REST bodies below, hub-as-code push (`ci.ts`), and the advisory
 * validate. Deliberately NOT justified by the shape of `MessageData` — an
 * eval's declared role never reaches `message_role` (the fire path hardcodes
 * that); it travels via the trigger row's `sender_type`.
 *
 * `message_history` is deliberately NOT constrained: a tool result there
 * answers a preceding assistant `tool_call`, which is what history is for. A
 * lone tool result as the *trigger* has nothing to answer.
 *
 * Takes the whole `input` (either shape — see `asEvalInputRecord`), not just
 * the role. An absent role is not a declaration, so it passes: this rules only
 * on what the author actually wrote, and supplying a default is the producer's
 * job, not the contract's.
 *
 * Returns a lowercase fragment starting `role must be …`, so a caller can
 * prefix its own subject (`eval "X": …`, `Message text …`) or use it bare.
 */
export function evalInputRoleError(input: unknown): string | null {
  const record = asEvalInputRecord(input);
  if (record === null) return null;

  const role = record.role;
  if (role === undefined || role === null) return null;
  if (typeof role === 'string' && (EVAL_INPUT_ROLES as readonly string[]).includes(role)) {
    return null;
  }
  return `role must be ${EVAL_INPUT_ROLE_LIST} (received ${describeRole(role)}) — an eval trigger is what the responder answers, and an assistant or tool role gives it nothing to answer`;
}

/**
 * `null` when a turn's `attachments` are well-formed, else the rejection.
 *
 * Same contract style as `evalInputRoleError` (gh #3371): takes the whole turn in
 * either shape (object or JSON string — see `asEvalInputRecord`), rules only on what
 * the author declared, and returns a lowercase fragment a caller can prefix with its
 * own subject. Shared by the REST create/update refines and the advisory validator so
 * the rejection text cannot drift between them. The hub-as-code push path
 * (`refineHubAsCodeEvals`) is NOT yet wired to it — attachment authoring over push
 * lands with the CLI half (gh #3365 PR 2), which is where that gate belongs.
 *
 * Checks, in order: array shape, per-turn cap, USER-ROLE-ONLY (the fire path derives
 * the trigger's sender from the turn role and injects files into the last user
 * message — an attachment on any other role would silently land on the wrong turn),
 * then each ref against `evalAttachmentRef` (whose hash regex is a security
 * boundary — the hash becomes an R2 key segment).
 */
export function evalTurnAttachmentsError(turn: unknown): string | null {
  const record = asEvalInputRecord(turn);
  if (record === null) return null;

  const attachments = record.attachments;
  if (attachments === undefined || attachments === null) return null;
  if (!Array.isArray(attachments)) return 'attachments must be an array';
  if (attachments.length === 0) return null;
  if (attachments.length > MAX_ATTACHMENTS_PER_MESSAGE) {
    return `attachments must have at most ${MAX_ATTACHMENTS_PER_MESSAGE} items`;
  }
  if (!turnMayCarryAttachments({ role: String(record.role), attachments })) {
    return ATTACHMENTS_USER_ONLY_MESSAGE;
  }
  for (let i = 0; i < attachments.length; i++) {
    const parsed = evalAttachmentRef.safeParse(attachments[i]);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      return `attachments[${i}].${issue?.path.join('.') || '?'} is invalid: ${issue?.message ?? 'malformed'}`;
    }
  }
  return null;
}

/**
 * `message_history` in either stored shape: array, or the JSON string the TEXT
 * column tolerates. Anything else is left to the rest of validation.
 */
function asEvalHistoryArray(history: unknown): unknown[] | null {
  const value = parseMaybeJson(history);
  return Array.isArray(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Scenario initial_state — seed user-scoped WayAI state before a run (gh #3430)
// ---------------------------------------------------------------------------

/**
 * The scopes an `initial_state` entry may seed. Only `user` is supported today:
 * user-scoped state is keyed by user and survives across a user's conversations,
 * which is precisely the memory an eval otherwise cannot set up (a recurring-user
 * shortcut, a "confirmed" flag, etc.). Conversation-scope state is already fresh
 * per run (each run is a new conversation), so seeding it is a separate, later ask —
 * kept out of the enum so an author can't declare a scope the runtime silently drops.
 */
export const EVAL_INITIAL_STATE_SCOPES = ['user'] as const;

/** Bound the per-scenario seed list so a passthrough/pushed row can't fan out an
 *  unbounded number of HubDO writes at schedule time. */
export const MAX_INITIAL_STATE_ENTRIES = 20;

/**
 * One user-scope state seed on a scenario. `slug` names an EXISTING user-scope
 * state definition in the hub (the seed writes a value, never a definition);
 * `value` is the JSON object written for the eval's synthetic user before its
 * trigger runs. The value is validated against the state's own `json_schema` at
 * seed time (HubDO `updateUserState`), so a malformed seed degrades the run with
 * a warning rather than poisoning anything — the same guard a live agent write hits.
 */
export const evalInitialStateEntry = z.object({
  slug: z.string().min(1).max(200),
  scope: z.enum(EVAL_INITIAL_STATE_SCOPES).default('user'),
  value: z.record(z.unknown()),
});
export type EvalInitialStateEntry = z.infer<typeof evalInitialStateEntry>;

/** `initial_state` in either stored shape: array, or the JSON string the TEXT
 *  column tolerates (eval rows persist JSON columns as text). */
function asEvalInitialStateArray(value: unknown): unknown[] | null {
  const parsed = parseMaybeJson(value);
  return Array.isArray(parsed) ? parsed : null;
}

/**
 * `null` when a scenario's `initial_state` is well-formed, else the rejection.
 *
 * Same contract style as `evalTurnAttachmentsError` (gh #3371): takes the whole
 * eval body in either shape (object or JSON string), rules only on what the author
 * declared (absent ⇒ passes), and returns a lowercase fragment a caller can prefix
 * with its own subject. Shared by the REST create/update refines, the advisory
 * validator, AND the hub-as-code push refine so the rejection text can't drift
 * between them.
 *
 * Checks, in order: array shape, per-scenario cap, then each entry against
 * `evalInitialStateEntry` (slug non-empty, scope in the supported enum, value a
 * JSON object). "The slug names a state that exists in the hub" is NOT checked here —
 * that is a cross-entity, runtime fact (the definition may be pushed in the same
 * batch), so a missing/invalid definition degrades the run with a warn at seed time
 * rather than failing authoring.
 */
export function evalInitialStateError(input: unknown): string | null {
  const record = asEvalInputRecord(input);
  if (record === null) return null;

  const initialState = record.initial_state;
  if (initialState === undefined || initialState === null) return null;
  const entries = asEvalInitialStateArray(initialState);
  if (!entries) return 'initial_state must be an array';
  if (entries.length === 0) return null;
  if (entries.length > MAX_INITIAL_STATE_ENTRIES) {
    return `initial_state must have at most ${MAX_INITIAL_STATE_ENTRIES} entries`;
  }
  const seenSlugs = new Set<string>();
  for (let i = 0; i < entries.length; i++) {
    const parsed = evalInitialStateEntry.safeParse(entries[i]);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const path = issue?.path.join('.') || '?';
      return `initial_state[${i}].${path} is invalid: ${issue?.message ?? 'malformed'}`;
    }
    // A duplicate slug is an authoring mistake: the second seed would clobber the
    // first at write time, so the declared intent is ambiguous — reject it.
    if (seenSlugs.has(parsed.data.slug)) {
      return `initial_state[${i}].slug "${parsed.data.slug}" is declared more than once`;
    }
    seenSlugs.add(parsed.data.slug);
  }
  return null;
}

/**
 * The synthetic, state-only user id an eval run seeds and reads user-scope state as.
 *
 * Deterministic from the run id so the SEED (route, at schedule time) and the
 * TEARDOWN (HubDO `clearEvalSeed`, at session finalize) agree on the id without
 * persisting a mapping. It is deliberately NOT the conversation's `user_id`, which an
 * eval conversation leaves NULL; this id carries ONLY the seeded state, via the
 * conversation's `eval_state_user_id` column. The `eval-seed:` prefix keeps it from
 * colliding with any real hub user id.
 *
 * That userlessness is no longer what makes an eval free: billing keys on the ORG, and
 * `userId` is attribution (gh #3831). A path that must not bill an eval says so — the
 * harness drain classifies its rows `MESSAGE_BILLING.unbilled` on `is_eval`. Two terms
 * are NOT yet exempt on that rule and still charge an eval run: the schedule fire's turn
 * initiation and native-turn DO residency.
 */
export function evalSeedStateUserId(runId: string): string {
  return `eval-seed:${runId}`;
}

// ---------------------------------------------------------------------------
// Scenario customer — the contact an eval run serves (#5083 RK2)
// ---------------------------------------------------------------------------

/**
 * The contact id a scenario's runs serve as their customer: what a customer-scoped toolset's
 * calls name in an eval, as a live channel names the contact behind its phone, Instagram id or
 * account. The contact id keys the customer's records in the base (`wayai_contact`), and the
 * toolset binding sends it as a UUID (`rekorBoundAttestation` in `rekor-toolset.ts`), so it is one
 * here.
 *
 * Honoured on preview hubs only (owner decision 125, Q3): a production hub's eval names no
 * customer, whatever its scenario declares, so its customer-scoped toolsets refuse it as a
 * conversation with no customer. On production, a declared id would let an eval author act as
 * any real customer.
 */
export const evalCustomerContactIdSchema = uuidSchema;

/**
 * `null` when a scenario's `customer_contact_id` is absent, `null`, or a UUID, else the rejection.
 * Same contract style as `evalInitialStateError`: takes the whole eval body in either shape,
 * rules only on what the author declared, and returns a lowercase fragment a caller prefixes.
 * Shared by the REST create/update bodies, the advisory validate and the hub-as-code push, which
 * applies it to a journey's `customer_contact_id` too.
 */
export function evalCustomerContactIdError(input: unknown): string | null {
  const record = asEvalInputRecord(input);
  if (record === null) return null;
  const value = record.customer_contact_id;
  if (value === undefined || value === null) return null;
  return evalCustomerContactIdSchema.safeParse(value).success
    ? null
    : 'customer_contact_id must be a contact id (a UUID)';
}

// ---------------------------------------------------------------------------
// Eval CRUD
// ---------------------------------------------------------------------------

/**
 * `null` when a declared eval name is a non-blank string, else the rejection.
 *
 * Blank names used to reach storage unchecked, and one blank-named eval crashed every
 * eval session results page in its hub (a Radix `<SelectItem value="">` in the
 * eval-name filter, gh #4546). The web now tolerates such a row (`toRadixValue`); this
 * rule stops new ones being written. An absent name is not a declaration — a partial
 * update leaves it alone. Shared by the REST create/update bodies and hub-as-code push
 * (`refineHubAsCodeEvals`); the advisory validate keeps its own, stricter
 * required-name check.
 */
export function evalNameError(name: unknown): string | null {
  if (name === undefined) return null;
  if (typeof name === 'string' && name.trim().length > 0) return null;
  return 'eval name must be a non-blank string';
}

/** A declared `eval_name` must be non-blank — see `evalNameError`. */
function refineEvalName(
  value: { eval: Record<string, unknown> },
  ctx: z.RefinementCtx,
): void {
  const error = evalNameError(value.eval.eval_name);
  if (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['eval', 'eval_name'], message: error });
  }
}

/** The eval body stays an open record — only the fields refined below are contracted. */
function refineEvalMessageTextRole(
  value: { eval: Record<string, unknown> },
  ctx: z.RefinementCtx,
): void {
  const error = evalInputRoleError(value.eval.message_text);
  if (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['eval', 'message_text', 'role'],
      message: error,
    });
  }
}

/** `initial_state` contract on the eval body — see `evalInitialStateError`. */
function refineEvalInitialState(
  value: { eval: Record<string, unknown> },
  ctx: z.RefinementCtx,
): void {
  const error = evalInitialStateError(value.eval);
  if (error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['eval', 'initial_state'],
      message: error,
    });
  }
}

/** `customer_contact_id` contract on the eval body — see `evalCustomerContactIdError`. */
function refineEvalCustomer(
  value: { eval: Record<string, unknown> },
  ctx: z.RefinementCtx,
): void {
  const error = evalCustomerContactIdError(value.eval);
  if (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['eval', 'customer_contact_id'], message: error });
  }
}

/** Attachments contract on the trigger and every history turn — see `evalTurnAttachmentsError`. */
function refineEvalAttachments(
  value: { eval: Record<string, unknown> },
  ctx: z.RefinementCtx,
): void {
  const triggerError = evalTurnAttachmentsError(value.eval.message_text);
  if (triggerError) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['eval', 'message_text', 'attachments'],
      message: triggerError,
    });
  }
  const history = asEvalHistoryArray(value.eval.message_history);
  if (!history) return;
  for (let i = 0; i < history.length; i++) {
    const error = evalTurnAttachmentsError(history[i]);
    if (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eval', 'message_history', i, 'attachments'],
        message: error,
      });
    }
  }
}

/** Create and update take the same body and the same rules, so a rule can't land on one only. */
const evalBody = z.object({
  eval: z.record(z.unknown()),
}).superRefine(refineEvalName).superRefine(refineEvalMessageTextRole).superRefine(refineEvalAttachments).superRefine(refineEvalInitialState)
  .superRefine(refineEvalCustomer);

export const createEvalBody = evalBody;
export const updateEvalBody = evalBody;

/**
 * Rows a single eval list request may ask for. HubDO clamps to the same number
 * independently — this refine exists so an over-limit caller gets a 400 that
 * names the cap instead of a silently truncated page. Above it, the DO's RPC
 * return can exceed Cloudflare's 32 MiB cap (Sentry WAYAI-BACKEND-7S).
 */
export const EVAL_LIST_MAX_LIMIT = 1000;

const evalListLimit = z.string().regex(/^\d+$/)
  .refine((v) => Number(v) <= EVAL_LIST_MAX_LIMIT, {
    message: `limit must be ${EVAL_LIST_MAX_LIMIT} or less`,
  })
  .optional();

export const getEvalsQuery = z.object({
  hub_id: z.string().uuid(),
  agent_id: z.string().uuid().optional(),
  enabled: z.string().regex(/^(true|false)$/).optional(),
  search: z.string().optional(),
  limit: evalListLimit,
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Toggle / Bulk Toggle
// ---------------------------------------------------------------------------

export const toggleEvalBody = z.object({
  enabled: z.boolean(),
});

export const bulkToggleEvalsBody = z.object({
  hub_id: z.string().uuid(),
  enabled: z.boolean(),
  filters: z.object({
    agent_id: z.string().uuid().optional(),
    search: z.string().optional(),
  }).optional(),
});

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export const pacingConfigSchema = z.object({
  preset: z.enum(EVAL_PACING_PRESET_NAMES),
  custom_interval_ms: z.number().int().min(PACING_INTERVAL_MIN_MS).max(PACING_INTERVAL_MAX_MS).optional(),
}).refine(
  (v) => v.preset !== 'custom' || typeof v.custom_interval_ms === 'number',
  { message: 'custom_interval_ms required when preset is "custom"' },
);

/**
 * Ephemeral scenario selection (gh #3748) — the exact subset of a set's enabled
 * scenarios one session runs, chosen at create time without editing or
 * publishing YAML. Absent → the whole set, which stays the default.
 *
 * It lives in `session_config` rather than on its own column deliberately: it is
 * a property of ONE launch, and storing it with the session makes the selection
 * an audit record — a later reader sees exactly which scenarios and repetitions
 * that session scored, and can reproduce it verbatim.
 *
 * `run_numbers` are the scenario's OWN 1-based run indices, never a renumbered
 * 1..N: run *i* resolves `variables.NAME[i-1]` (see `eval-variables.ts`), so
 * `--runs 2,6` must materialize runs 2 and 6 against those same fixtures. A
 * renumbering would silently re-point the selection at other data.
 */
export const EVAL_MAX_SELECTED_SCENARIOS = 200;

export const evalScenarioSelectionEntrySchema = z.object({
  eval_id: z.string().uuid(),
  run_numbers: z
    .array(z.number().int().min(1).max(EVAL_MAX_RUNS_PER_SCENARIO))
    .min(1)
    .max(EVAL_MAX_RUNS_PER_SCENARIO)
    .refine((runs) => new Set(runs).size === runs.length, {
      message: 'run_numbers must not repeat a run',
    })
    .optional(),
}).strict();

export const evalScenarioSelectionSchema = z
  .array(evalScenarioSelectionEntrySchema)
  .min(1)
  .max(EVAL_MAX_SELECTED_SCENARIOS)
  .refine(
    (entries) => new Set(entries.map((e) => e.eval_id)).size === entries.length,
    { message: 'scenario_selection must not name the same scenario twice' },
  );

/**
 * The selection after the run route resolves it — every run number explicit, so
 * HubDO materializes exactly what was validated instead of re-deriving it from
 * rows read a moment later. Internal (route → DO), never a wire shape.
 */
export interface EvalScenarioRunSelection {
  eval_id: string;
  run_numbers: number[];
}

// `pacing` and `scenario_selection` are the client-supplied session_config
// fields today; the backend also writes `agent` and `scenario_set_id` snapshots
// during run-session. `passthrough()` on both layers preserves those
// server-managed fields and keeps room for future client-side keys without
// contract churn.
export const sessionConfigSchema = z.object({
  pacing: pacingConfigSchema.optional(),
  scenario_selection: evalScenarioSelectionSchema.optional(),
  /**
   * CALL MODE (realtime voice calls, plan §12.1): every turn this session fires runs as a call
   * turn would — the pilot gets the call-mode addendum, and the reply gate takes no rewrite (a
   * draft it would have rewritten is held, as on a call). Delivery and scoring stay a text
   * eval's. A property of the launch, like the two keys above.
   */
  call_mode: z.boolean().optional(),
  // Server-owned immutable clock.
  date_context: z.never().optional(),
  date_context_claimed: z.never().optional(),
  seed_cleanup: z.never().optional(),
  // Per-run finalization deadline for the HubDO watchdog (gh #2591). A started
  // run idle past this many ms is reaped to `failed`. Omitted → backend default
  // (`EVAL_DEFAULT_RUN_DEADLINE_MS`).
  run_deadline_ms: z.number().int().min(EVAL_RUN_DEADLINE_MIN_MS).max(EVAL_RUN_DEADLINE_MAX_MS).optional(),
  seed: z.never().optional(),
}).passthrough();

export const createSessionBody = z.object({
  session: z.object({
    session_name: z.string().trim().min(1),
    session_config: sessionConfigSchema.optional(),
  }).passthrough(),
});

export const getSessionsQuery = z.object({
  hub_id: z.string().uuid(),
  status: z.string().optional(),
  search: z.string().optional(),
  limit: evalListLimit,
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Results & Runs
// ---------------------------------------------------------------------------

export const getSessionResultsQuery = z.object({
  hub_id: z.string().uuid(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

export const getSessionRunsQuery = z.object({
  hub_id: z.string().uuid(),
  eval_id: z.string().uuid().optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export const evalAnalyticsQuery = z.object({
  hub_id: z.string().uuid(),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  periodicity: z.enum(['daily', 'weekly', 'monthly']).optional(),
  eval_id: z.string().uuid().optional(),
  session_id: z.string().uuid().optional(),
});

export const getSessionCostQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Compare sessions
// ---------------------------------------------------------------------------

export const compareSessionsQuery = z.object({
  hub_id: z.string().uuid(),
  session_a: z.string().uuid(),
  session_b: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Read-only SQL (CLI agent surface — POST /sql, GET /sql/schema)
// Scopes the readable tables to `is_eval = true` (mirrors /api/analytics/sql).
// The schema endpoint below describes `conversation` only.
// ---------------------------------------------------------------------------

export const evalsSqlBody = z.object({
  hub_id: z.string().uuid(),
  // Hard size cap before the validator runs — keeps lex/parse work bounded.
  sql: z.string().min(1).max(8000),
  limit: z.number().int().positive().max(10000).optional(),
});

export const evalsSqlSchemaQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const exportResultsQuery = z.object({
  format: z.enum(['csv', 'json']).default('csv'),
});

// ---------------------------------------------------------------------------
// Validate
// ---------------------------------------------------------------------------

export const validateEvalBody = z.object({
  eval: z.record(z.unknown()),
});

// ---------------------------------------------------------------------------
// Hub Agents
// ---------------------------------------------------------------------------

export const hubAgentsQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Scenario sets
// ---------------------------------------------------------------------------

export const createScenarioSetBody = z.object({
  hub_id: z.string().uuid(),
  set_name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  enabled: z.boolean().optional(),
});

export const updateScenarioSetBody = z.object({
  set_name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).nullable().optional(),
  enabled: z.boolean().optional(),
});

export const scenarioSetsHubQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Production replay — create scenario from a real conversation
// ---------------------------------------------------------------------------

export const createScenarioFromConversationBody = z.object({
  hub_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  scenario_set_id: z.string().uuid(),
  // Becomes the created eval's `eval_name`, so it follows `evalNameError`.
  scenario_name: z.string().max(200).refine((name) => evalNameError(name) === null, 'scenario_name must be a non-blank string'),
  evaluator_instructions: z.string().max(4000).optional(),
});

// Capture a real conversation as an eval JOURNEY — the WHOLE transcript, not
// just the last exchange. A journey owns its managed scenario set, so there is
// no `scenario_set_id` here (cf. the scenario body above).
export const createJourneyFromConversationBody = z.object({
  hub_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200),
  evaluator_instructions: z.string().max(4000).optional(),
});

// ---------------------------------------------------------------------------
// Eval attachments — a file on a scenario/journey user turn
// ---------------------------------------------------------------------------

// The attachment's content address, `EVAL_ATTACHMENT_HASH_REGEX`, is declared in `./storage-keys`.

/**
 * One attachment REFERENCE on an eval/journey user turn. The bytes live in R2 at
 * `{hub_id}/eval-attachments/{hash}` (resource bucket — reaped with the hub, and
 * deliberately NOT under any conversation prefix, which eval teardown prefix-deletes
 * out from under sibling runs). Turns carry only this reference: base64 on a turn
 * would ride every frozen `scenario_snapshot` and poison the CI diff engine, which
 * compares turns by JSON.stringify.
 *
 * NOT a "fixture" — that word is taken: `fixture`/`target_base` on eval/journey rows
 * name a Rekor seed dependency (`packages/core/src/eval-fixture.ts`).
 *
 * - `hash` is a SECURITY BOUNDARY, not an identifier convention: it becomes an R2 key
 *   segment, so anything but strict lowercase-hex-64 is a key-injection vector. The
 *   same regex is re-checked where the key is built and at the eval fire path — the
 *   last line of defense, since eval create/update bodies are passthrough.
 * - `mime_type` is REQUIRED because the LLM file path categorizes by MIME and
 *   SILENTLY DROPS a file that is neither image nor document by MIME sniff
 *   (`fileProcessor.processMessageFiles`) — an optional mime would let an authored
 *   attachment vanish from the model's view with no error anywhere.
 */
export const evalAttachmentRef = z.object({
  hash: z.string().regex(EVAL_ATTACHMENT_HASH_REGEX),
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
  file_size: z.number().int().positive().optional(),
});
export type EvalAttachmentRef = z.infer<typeof evalAttachmentRef>;

/**
 * Attachments are valid on USER turns only. The eval fire path derives the trigger
 * message's `sender_type` from the turn role, and multimodal file content is injected
 * into the LAST USER message of the assembled history — an attachment on an
 * assistant/system/tool turn would silently attach the file to the wrong turn.
 */
export const turnMayCarryAttachments = (turn: { role: string; attachments?: unknown[] }): boolean =>
  !turn.attachments || turn.attachments.length === 0 || turn.role === 'user';

/** The one wording for the user-turns-only rejection — shared by every gate. */
export const ATTACHMENTS_USER_ONLY_MESSAGE = 'attachments are only valid on user turns';

/**
 * The VALID attachment hashes on one turn — the single per-turn extractor shared by
 * every place that collects referenced hashes (the CI push refine, the apply upload,
 * hub-branching byte copy). Each ref is `evalAttachmentRef.safeParse`d, so a malformed
 * hash (the R2 key-injection boundary) never reaches a key builder. Accepts either turn
 * shape (object or JSON string, via `asEvalInputRecord`).
 */
export function collectTurnAttachmentHashes(turn: unknown): string[] {
  const atts = asEvalInputRecord(turn)?.attachments;
  if (!Array.isArray(atts)) return [];
  const hashes: string[] = [];
  for (const a of atts) {
    const parsed = evalAttachmentRef.safeParse(a);
    if (parsed.success) hashes.push(parsed.data.hash);
  }
  return hashes;
}

// ---------------------------------------------------------------------------
// Eval journeys — a happy-path transcript that materializes into single-turn evals
// ---------------------------------------------------------------------------

const journeyIdParam = z.object({ id: z.string().uuid() });

const journeyToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('function'),
  function: z.object({ name: z.string(), arguments: z.string() }),
});

// One transcript turn — the replayed message-history shape plus an optional
// stable `step_id` (server-minted on each step's concluding assistant turn).
//
// `attachments` is in the SCHEMA, not just the response type, because z.object
// STRIPS unknown keys: before it was declared here, any journey save through
// `updateJourneyBody` silently deleted CLI-authored attachments from the
// transcript — a data-loss path, not a validation gap (gh #3365).
const journeyTurnSchema = z.object({
  role: z.enum(['system', 'user', 'assistant', 'tool']),
  content: z.string().nullable().optional(),
  tool_calls: z.array(journeyToolCallSchema).optional(),
  tool_call_id: z.string().optional(),
  step_id: z.string().optional(),
  attachments: z.array(evalAttachmentRef).max(MAX_ATTACHMENTS_PER_MESSAGE).optional(),
}).refine(turnMayCarryAttachments, {
  message: ATTACHMENTS_USER_ONLY_MESSAGE,
});

const journeyStepOverrideSchema = z.object({
  number_of_runs: z.number().int().positive().max(EVAL_MAX_RUNS_PER_SCENARIO).optional(),
  evaluator_instructions: z.string().max(4000).nullable().optional(),
});

// Eval-session seed fixture (gh #3090). Nullable on update so clearing the key
// removes the dependency; blank-string is normalized to "no fixture" downstream.
const fixtureField = z.string().max(EVAL_FIXTURE_NAME_MAX_LENGTH);

export const createJourneyBody = z.object({
  hub_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200),
  responder_agent_id: z.string().uuid(),
  transcript: z.array(journeyTurnSchema).default([]),
  step_config: z.record(journeyStepOverrideSchema).optional(),
  evaluator_instructions: z.string().max(4000).optional(),
  fixture: fixtureField.optional(),
  target_base: fixtureField.optional(),
});

export const updateJourneyBody = z.object({
  hub_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200).optional(),
  responder_agent_id: z.string().uuid().optional(),
  transcript: z.array(journeyTurnSchema).optional(),
  step_config: z.record(journeyStepOverrideSchema).optional(),
  evaluator_instructions: z.string().max(4000).nullable().optional(),
  enabled: z.boolean().optional(),
  fixture: fixtureField.nullable().optional(),
  target_base: fixtureField.nullable().optional(),
});

// (journey list reuses the shared `hubIdQuery`)

// Re-export param schemas
export { evalIdParam, sessionIdParam, scenarioSetIdParam, hubIdQuery, journeyIdParam };

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type EvalScenarioSelection = z.infer<typeof evalScenarioSelectionSchema>;
export type EvalsSqlBody = z.infer<typeof evalsSqlBody>;
export type CreateJourneyBody = z.infer<typeof createJourneyBody>;
export type UpdateJourneyBody = z.infer<typeof updateJourneyBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

// -- Status types --

export type SessionStatus = 'draft' | 'agents_responding' | 'awaiting_review' | 'completed' | 'failed' | 'cancelled';
export type RunStatus = 'pending' | 'running' | 'agent_responded' | 'completed' | 'failed';

// -- Tool call structures --

export interface ToolCallResponse {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
}

// -- Message structures --

export interface MessageHistoryItemResponse {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: ToolCallResponse[];
  tool_call_id?: string;
  /** User turns only — see `evalAttachmentRef` / `turnMayCarryAttachments`. */
  attachments?: EvalAttachmentRef[];
}

export interface ExpectedResponseData {
  role: 'assistant';
  content: string | null;
  tool_calls?: ToolCallResponse[];
}

export interface EvalResponseData {
  role: 'assistant';
  content: string;
  /**
   * The tool calls the agent made this turn (name + arguments). Present for
   * tool-call evals so `eval-results --runs` is self-contained — the verdict,
   * the reasoning, AND the actual call — without opening the conversation
   * (gh #2597). Omitted when the agent made no tool calls. Same shape as
   * `ExpectedResponseData.tool_calls` so expected vs. actual can be diffed.
   */
  tool_calls?: ToolCallResponse[];
  metadata?: {
    response_time_ms?: number;
    model_used?: string;
    tokens_used?: number;
  };
}

// -- Entity response types --

/** Full eval row as returned by the eval table. Scenarios MUST belong to a scenario set. */
export interface EvalResponse {
  eval_id: string;
  created_at: string;
  updated_at: string;
  hub_id: string;
  user_id: string;
  responder_agent_id: string;
  eval_name: string;
  scenario_set_id: string;
  evaluator_instructions?: string;
  message_history: MessageHistoryItemResponse[];
  message_text: MessageHistoryItemResponse;
  message_expected_response: ExpectedResponseData;
  number_of_runs: number;
  enabled: boolean;
  created_by: string;
  /** Non-null for evals derived from an eval journey (server-managed). */
  journey_id?: string | null;
  /** 0-based display order of the step within its journey (for step-by-step reports). */
  journey_step_index?: number | null;
  /** Eval-session seed fixture this scenario's set depends on (gh #3090). */
  fixture?: string | null;
  /** Rekor base the fixture seeds against — required alongside `fixture` (gh #3090). */
  target_base?: string | null;
  /** User-scope state seeded for the eval's synthetic user before the trigger runs (gh #3430). */
  initial_state?: EvalInitialStateEntry[] | null;
  /** The contact the scenario's runs serve as their customer, on a preview hub (#5083 RK2). */
  customer_contact_id?: string | null;
}

/** One turn of a journey's happy-path transcript (message-history shape + optional step_id). */
export interface JourneyTranscriptTurnResponse extends MessageHistoryItemResponse {
  step_id?: string;
}

/** Per-step override config, keyed by step_id. */
export interface JourneyStepOverrideResponse {
  number_of_runs?: number;
  evaluator_instructions?: string | null;
}

/** Full eval_journey row. */
export interface JourneyResponse {
  journey_id: string;
  journey_name: string;
  scenario_set_id: string;
  responder_agent_id: string;
  transcript: JourneyTranscriptTurnResponse[];
  step_config: Record<string, JourneyStepOverrideResponse>;
  evaluator_instructions?: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Eval-session seed fixture the journey depends on (gh #3090). */
  fixture?: string | null;
  /** Rekor base the fixture seeds against — required alongside `fixture` (gh #3090). */
  target_base?: string | null;
}

/** Frozen agent configuration captured at session-launch time. */
export interface AgentConfigSnapshot {
  agent_id: string;
  agent_name: string | null;
  agent_role: string | null;
  instructions: string | null;
  response_format: string | null;
  schema_name: string | null;
  schema_json: unknown;
  agent_settings: unknown;
  enabled: boolean;
  connection: { connection_id: string | null; connector_id: string | null; model_name: string | null } | null;
  tools: Array<Record<string, unknown>>;
  resources: Array<{ resource_id: string; version?: string | null }>;
}

/**
 * What a TERMINAL eval run's outcome actually was. Separates the two ways a run
 * can produce no verdict — the harness died (`execution_error`) or the run
 * completed but the evaluator's verdict is unusable (`evaluation_invalid`) —
 * from a genuine behavioral miss (`assertion_failure`). Only `pass` and
 * `assertion_failure` carry a verdict, so only they belong in a pass rate.
 */
export const EVAL_RUN_OUTCOME_KINDS = [
  'pass',
  'assertion_failure',
  'execution_error',
  'evaluation_invalid',
] as const;
export type EvalRunOutcomeKind = (typeof EVAL_RUN_OUTCOME_KINDS)[number];

/**
 * The subset a failing caller may declare; the rest are derived from the verdict.
 * `satisfies` keeps it a real subset — renaming a kind above breaks here rather
 * than silently leaving this array naming a value the enum no longer has.
 */
export const EVAL_RUN_ERROR_KINDS = [
  'execution_error',
  'evaluation_invalid',
] as const satisfies readonly EvalRunOutcomeKind[];
export type EvalRunErrorKind = (typeof EVAL_RUN_ERROR_KINDS)[number];

/**
 * Single source for classifying a terminal run, shared by every surface that
 * counts runs. `stored_kind` is the value the harness recorded at the failure
 * seam; it is validated against the enum because it round-trips through the
 * free-form `score_dimensions` JSON, where an evaluator-authored key of the same
 * name could otherwise land.
 *
 * A `failed` row without a usable stored kind is `execution_error`: `failed` has
 * only ever meant harness-level death (the write path forces `response_match`
 * null there), never a behavioral miss — so pre-taxonomy rows classify correctly.
 */
export function deriveEvalRunOutcomeKind(args: {
  result_status: string | null | undefined;
  response_match: boolean | null | undefined;
  stored_kind?: unknown;
}): EvalRunOutcomeKind {
  const stored = typeof args.stored_kind === 'string'
    && (EVAL_RUN_OUTCOME_KINDS as readonly string[]).includes(args.stored_kind)
    ? (args.stored_kind as EvalRunOutcomeKind)
    : null;
  if (args.result_status === 'failed') {
    return stored === 'evaluation_invalid' ? 'evaluation_invalid' : 'execution_error';
  }
  if (args.response_match === true) return 'pass';
  if (args.response_match === false) return 'assertion_failure';
  // Completed, but the evaluator returned no boolean verdict — nothing to score.
  return 'evaluation_invalid';
}

/**
 * The terminal run counts every eval surface reports, as they appear on the
 * wire. `errored_runs`/`invalid_runs` are optional here ONLY so a released CLI
 * binary pointed at an older API still renders; the API always sends them.
 */
export interface EvalRunCounts {
  successful_runs: number;
  failed_runs: number;
  assertion_failure_runs?: number;
  errored_runs?: number;
  invalid_runs?: number;
}

/**
 * Runs that carry a verdict — the pass-rate denominator, and the ONLY set a
 * percentage may be computed over. A run that errored was never scored, so
 * counting it would understate the rate against runs that were.
 *
 * Falls back to `failed_runs` when the split is absent (an older API), which is
 * the pre-taxonomy behaviour rather than a silently emptier denominator.
 * Always report `unprovenRunCount` beside any rate built on this, so a shrunken
 * sample is visible rather than reading as a clean sweep.
 */
export function evaluatedRunCount(counts: EvalRunCounts): number {
  return counts.successful_runs + (counts.assertion_failure_runs ?? counts.failed_runs);
}

/** Terminal runs that produced no verdict (infrastructure death or unusable evaluation). */
export function unprovenRunCount(counts: EvalRunCounts): number {
  return (counts.errored_runs ?? 0) + (counts.invalid_runs ?? 0);
}

/**
 * Every run that reached a terminal state, whatever its outcome — the PROGRESS
 * denominator, never the pass-rate one. `failed_runs` is already the union of
 * non-passing terminal runs, so this stays correct against an older API too.
 */
export function terminalRunCount(counts: EvalRunCounts): number {
  return counts.successful_runs + counts.failed_runs;
}

/** Internal RPC input for `HubDO.completeEvalRun` (not a public route). */
export interface CompleteEvalRunInput {
  run_id: string;
  status: 'completed' | 'failed';
  /**
   * Why the run died, on the `failed` path only. The seam that catches the
   * failure knows this; without it every infrastructure death is indistinguishable
   * from a behavioral miss downstream. Defaults to `execution_error` when omitted.
   */
  error_kind?: EvalRunErrorKind;
  agent_response?: string;
  /** Tool calls the agent made this turn — persisted alongside `agent_response` (gh #2597). */
  agent_tool_calls?: ToolCallResponse[];
  execution_time_ms: number;
  error_message?: string;
  score_dimensions?: Record<string, unknown>;
  response_match?: boolean | null;
  /**
   * The responder's evaluated assistant message id and the message_evaluator's
   * verdict message id, both in the run's eval conversation. Persisted on the run
   * so consumers can jump straight to a turn's LLM observability
   * (`wayai conversations <conv> observability --message-id <id>`) without first
   * listing the conversation. Omitted on the failed path (no turn produced).
   */
  response_message_id?: string;
  evaluator_message_id?: string;
}

/** Eval scenario set row. */
export interface ScenarioSetResponse {
  scenario_set_id: string;
  set_name: string;
  description?: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Non-null for sets owned by an eval journey (server-managed, read-only in the UI). */
  journey_id?: string | null;
}

/** Eval with joined agent data, as returned by list endpoint */
export interface EvalWithAgentResponse extends EvalResponse {
  agent?: {
    agent_id: string;
    agent_name: string;
    agent_role: string;
  } | null;
}

/** Public server-owned config; invalid legacy values are omitted. */
export const evalSessionConfigResponseSchema = z.object({
  agent: z.custom<AgentConfigSnapshot>().optional(),
  scenario_set_id: z.string().optional().catch(undefined),
  date_context: evalDateContextSchema.optional().catch(undefined),
  /** Resolved server-side seed attribution. */
  seed: z.object({
    fixture: z.string(),
    target_base: z.string().nullable().optional(),
    connection_id: z.string().nullable().optional(),
  }).optional(),
  // Internal lifecycle fields: never expose them as response data.
  date_context_claimed: z.unknown().optional().transform(() => undefined),
  seed_cleanup: z.unknown().optional().transform(() => undefined),
  pacing: pacingConfigSchema.optional().catch(undefined),
  /**
   * The exact scenarios and repetitions this session ran (gh #3748), or absent
   * for a whole-set session. Declared — not merely carried by `passthrough()` —
   * because it is the session's audit record: a reader must be able to tell what
   * was scored, and reproduce it. `.catch` only omits an unreadable legacy value
   * from the RESPONSE; the run path refuses such a session outright.
   */
  scenario_selection: evalScenarioSelectionSchema.optional().catch(undefined),
  /** The session ran its turns in call mode (`sessionConfigSchema.call_mode`). */
  call_mode: z.boolean().optional().catch(undefined),
}).passthrough();

/** Public eval session row. */
export const evalSessionResponseSchema = z.object({
  eval_session_id: z.string(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
  hub_id: z.string(),
  session_name: z.string(),
  session_description: z.string().optional(),
  session_status: z.enum(['draft', 'agents_responding', 'awaiting_review', 'completed', 'failed', 'cancelled']),
  started_at: timestampSchema.optional(),
  completed_at: timestampSchema.optional(),
  total_evals: z.number(),
  total_runs: z.number(),
  successful_runs: z.number(),
  /**
   * Every terminal run that did NOT pass, whatever the reason. Unchanged
   * meaning — deliberately NOT narrowed to behavioral misses, so a released CLI
   * that predates the split cannot read an infrastructure wipeout as a clean
   * sweep. Use `assertion_failure_runs` for the narrow count.
   */
  failed_runs: z.number(),
  /** The `failed_runs` breakdown by cause (see `EvalRunOutcomeKind`); sums to it. */
  assertion_failure_runs: z.number(),
  errored_runs: z.number(),
  invalid_runs: z.number(),
  created_by: z.string(),
  /** Optional — sessions created pre-PR2 may not have this. */
  scenario_set_id: z.string().nullable().optional(),
  /** Launch-time snapshot. */
  session_config: evalSessionConfigResponseSchema.optional(),
}).passthrough();

export type EvalSessionResponse = z.infer<typeof evalSessionResponseSchema>;

/**
 * The `session_config` subset `GET /api/evals/sessions` returns.
 *
 * The list projects the launch snapshot down to identity in SQL: carrying the
 * whole thing per row — `instructions`, every tool inlined with its description,
 * resources, the frozen `scenario_selection` — kept that response over
 * Cloudflare's 32 MiB RPC cap (Sentry WAYAI-BACKEND-7S). The fat fields are
 * reachable ONLY through `GET /api/evals/sessions/:id`.
 *
 * Declared separately from `evalSessionConfigResponseSchema` — rather than
 * letting the list reuse it — so that reading a projected-away field off a list
 * row is a compile error instead of a blank render. `AgentConfigSnapshot`
 * declares its fat fields as PRESENT, so one shared type would have the list
 * advertise twelve keys while returning three.
 */
export const evalSessionListConfigSchema = z.object({
  agent: z.object({
    agent_id: z.string().nullable().optional(),
    agent_name: z.string().nullable().optional(),
    agent_role: z.string().nullable().optional(),
  }).optional(),
  scenario_set_id: z.string().optional().catch(undefined),
});

/** One row of `GET /api/evals/sessions` — a session with the slim config above. */
export const evalSessionListItemSchema = evalSessionResponseSchema
  .omit({ session_config: true })
  .extend({ session_config: evalSessionListConfigSchema.optional() })
  .passthrough();

export type EvalSessionListItem = z.infer<typeof evalSessionListItemSchema>;

/** Session result row with optional joined eval */
export interface EvalSessionResultResponse {
  eval_session_result_id: string;
  created_at: string;
  updated_at: string;
  eval_session_id: string;
  eval_id: string;
  total_runs: number;
  successful_runs: number;
  /** Every terminal run that did NOT pass — unchanged meaning; see the session schema. */
  failed_runs: number;
  /** The `failed_runs` breakdown by cause (see `EvalRunOutcomeKind`); sums to it. */
  assertion_failure_runs: number;
  errored_runs: number;
  invalid_runs: number;
  pending_runs: number;
  running_runs: number;
  avg_execution_time_ms?: number;
  min_execution_time_ms?: number;
  max_execution_time_ms?: number;
  aggregated_scores?: Record<string, { avg: number; min: number; max: number }>;
  success_rate: number;
  eval_status: RunStatus;
  started_at?: string;
  completed_at?: string;
  eval?: EvalResponse | null;
}

/** Individual run result row with optional joined eval */
export interface EvalSessionRunResponse {
  eval_session_run_id: string;
  created_at: string;
  updated_at: string;
  eval_session_id: string;
  eval_id: string;
  conversation_id?: string;
  /**
   * The responder's evaluated assistant message id and the message_evaluator's
   * verdict message id within `conversation_id`. Let consumers (CLI, CI) drill
   * straight into a turn's LLM observability without listing the conversation
   * first. Absent on runs that predate this capture, or that never produced the
   * respective turn (e.g. a failed run has no evaluator message).
   */
  response_message_id?: string;
  evaluator_message_id?: string;
  run_number: number;
  eval_response: EvalResponseData;
  /**
   * The expected response frozen on the run's `scenario_snapshot` at
   * materialize time (NOT the live eval row, which may have drifted since). Lets
   * consumers render an expected-vs-actual diff for a failing run without a
   * second fetch (gh #2813). Omitted when the snapshot carried no expected.
   */
  expected_response?: ExpectedResponseData;
  /**
   * The scenario's frozen multi-turn `message_history`, captured per-run in the
   * `eval_session_run.scenario_snapshot` at session-launch time and replayed to
   * the responder ahead of the trigger turn (gh #2625). Surfaced as the
   * eval-details "conversation so far" block so a reviewer judges the response
   * against the full thread that preceded the evaluated turn — the live `message`
   * table only holds the trigger + the agent's reply, never this history. Omitted
   * when the scenario had no prior turns.
   */
  scenario_history?: MessageHistoryItemResponse[];
  response_match: boolean | null;
  /**
   * What this run's outcome actually was. Absent while the run is non-terminal
   * (nothing to classify yet); on a terminal run it distinguishes a behavioral
   * miss from an infrastructure death or an unusable verdict, which
   * `response_match: null` alone cannot.
   */
  outcome_kind?: EvalRunOutcomeKind;
  response_comment?: string;
  execution_time_ms?: number;
  run_status: RunStatus;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  scores?: Record<string, number>;
  metadata?: Record<string, unknown>;
  eval?: {
    eval_id: string;
    eval_name: string;
    responder_agent_id: string;
  } | null;
}

// -- Analytics response types --

export interface AgentPerformanceResponse {
  agent_id: string;
  agent_name: string;
  success_rate: number;
  total_runs: number;
  avg_execution_time: number;
}

export interface TrendDataResponse {
  date: string;
  success_rate: number;
  total_runs: number;
  avg_execution_time: number;
}

export interface ScoreDimensionData {
  dimension: string;
  avg_score: number;
  min_score: number;
  max_score: number;
  count: number;
}

export interface EvalAnalyticsData {
  totalEvals: number;
  enabledEvals: number;
  totalSessions: number;
  completedSessions: number;
  overallSuccessRate: number;
  avgExecutionTime: number;
  topPerformingAgents: AgentPerformanceResponse[];
  recentTrends: TrendDataResponse[];
  scoreDimensions: ScoreDimensionData[];
}

// -- Reference types (for dropdowns) --

export interface EvalAgentReference {
  agent_id: string;
  agent_name: string;
  agent_role: string;
}

export interface EvalHubReference {
  hub_id: string;
  hub_name: string;
  hub_description?: string;
}

// -- Pagination --

export interface EvalPaginationResponse {
  limit: number;
  offset: number;
  has_more: boolean;
}

// =============================================================================
// ENDPOINT RESPONSE WRAPPERS
// =============================================================================

export interface GetEvalsResponse {
  success: true;
  data: {
    evals: EvalWithAgentResponse[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface CreateEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface UpdateEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface DeleteEvalResponse {
  success: true;
  message: string;
}

export interface DeleteEvalErrorResponse {
  success: false;
  error: 'not_found';
  message: string;
}

export interface ToggleEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface BulkToggleEvalsResponse {
  success: true;
  data: {
    updated_count: number;
    updated_eval_ids: string[];
  };
  message: string;
}

export interface GetSessionsResponse {
  success: true;
  data: {
    /** Slim rows — the launch snapshot's fat fields need `GET /sessions/:id`. */
    sessions: EvalSessionListItem[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface CreateSessionResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

/**
 * Rekor's in-band seed-lease ledger warnings (gh #3745). The ledger is permanent
 * and capped at 1,000 rows per preview base; Rekor emits these as the base fills
 * so the operator can replace the preview before acquire starts failing.
 */
export const SEED_LEASE_WARNINGS = [
  'SEED_LEASE_CAPACITY_80_PERCENT',
  'SEED_LEASE_CAPACITY_95_PERCENT',
] as const;
export type SeedLeaseWarning = (typeof SEED_LEASE_WARNINGS)[number];

/**
 * Every failure `POST /sessions/:id/run` can report.
 *
 * The four seed-lease refusals are all 409 but differ in whether WAITING helps —
 * which is why they are separate codes, and why a client MUST discriminate on
 * the code rather than the status:
 *   • `fixture_target_in_use`           — another live session holds the base; it
 *                                         frees itself, so waiting works.
 *   • `fixture_seed_unavailable`        — Rekor answered a transient 5xx/429 (its
 *                                         documented, PRE-STORAGE backpressure),
 *                                         so nothing landed and an identical
 *                                         retry is safe. Waiting works.
 *   • `fixture_lease_not_owned`         — held under a credential this hub no
 *                                         longer has (the seed token was
 *                                         rotated). Only `rekor admin
 *                                         eval-leases release` clears it.
 *   • `fixture_lease_capacity_exceeded` — the preview's permanent lease ledger is
 *                                         full; the preview must be replaced.
 *
 * `fixture_seed_unavailable` (gh #3751) is deliberately NOT folded into
 * `fixture_seed_failed`: that code means "your configuration is wrong" and is
 * terminal, and classifying transient backpressure as terminal is what broke the
 * sequential/CI workflow the lease work shipped to enable. Retries reuse the
 * session id, and the backend replays the SAME lease tuple; minting a fresh one
 * against an acquire that did land is the permanent wedge the replay design
 * exists to dissolve.
 */
export const RUN_SESSION_ERRORS = [
  // Hub readiness (validateEvalReady)
  'no_message_evaluator',
  'missing_response_match',
  'verdict_before_reasoning',
  // Session / scenario-set preconditions
  'session_not_found',
  'invalid_session_status',
  // The set had nothing enabled when the launch was validated. A set emptied AFTER
  // that surfaces as `scenario_selection_invalid`, never as this code.
  'no_enabled_scenarios',
  'no_responder_agent',
  // A planned scenario's responder is a `pilot_voice` agent. The voice layer answers
  // live calls only, never a text turn, so a text eval cannot run it (realtime voice
  // calls, gh #4418). Refused at launch, before anything is written.
  'voice_agent_responder',
  'too_many_runs',
  'insufficient_variables',
  // A stored selection the set cannot satisfy, AND — for any launch, narrowed or
  // not — a plan the set stopped being able to satisfy while the session was
  // starting (gh #3758). Clients surface `message`, which names the scenarios.
  'scenario_selection_invalid',
  // Seed fixture resolution + Rekor lease
  'fixture_mismatch',
  'fixture_override_invalid',
  'fixture_seed_failed',
  'fixture_seed_unavailable',
  'fixture_target_in_use',
  'fixture_lease_not_owned',
  'fixture_lease_capacity_exceeded',
] as const;
export type RunSessionError = (typeof RUN_SESSION_ERRORS)[number];

/**
 * The subset the HubDO launch boundary itself reports — every run-session failure
 * except the ones the route settles before it (hub readiness, the responder agent,
 * the run ceiling, variable sufficiency).
 *
 * Declared here because both sides of that boundary spell the union: HubDO's return
 * type and the route's annotation on the RPC result. That annotation is an `as` cast,
 * so two hand-written copies drift silently — the same hazard `RunSessionErrorDetails`
 * below is centralized against.
 */
export type EvalLaunchError = Exclude<
  RunSessionError,
  'no_message_evaluator' | 'missing_response_match' | 'verdict_before_reasoning'
  | 'no_responder_agent' | 'voice_agent_responder' | 'too_many_runs' | 'insufficient_variables'
>;

/** Identity of the eval session currently holding the fixture, when resolvable. */
export interface SeedLeaseHolder {
  hub_id: string;
  session_id: string;
  acquired_at: string | null;
}

/**
 * The `details` envelope on a REFUSED `POST /sessions/:id/run` — the machine-readable
 * half of a refusal, next to the human sentence in `message`. Declared here, not
 * re-spelled per app: both fields are read across a process boundary (backend writes,
 * CLI + web read), and an inline shape on each side type-checks even after the other
 * side renames a field, so the drift is silent by construction.
 *
 * Every field is optional and populated only for the refusals it makes sense for.
 */
export interface RunSessionErrorDetails {
  /** Set on `fixture_target_in_use` when the caller is entitled to know the holder. */
  holder?: SeedLeaseHolder;
  /**
   * Rekor's own `Retry-After`, in seconds, forwarded on `fixture_seed_unavailable`
   * (gh #3751). A FLOOR for the next attempt, never the whole schedule: a client
   * waits at least this long and keeps its own backoff above it. Absent when Rekor
   * sent no header or an unparseable one — then the client uses its backoff alone.
   *
   * It rides the BODY rather than an HTTP `Retry-After` header because the refusal
   * is a 409 (see the status map in `routes/api/evals`), and a `Retry-After` on a
   * 409 is not something a generic HTTP client would honor anyway.
   */
  retry_after_seconds?: number;
}

export interface RunSessionResponse {
  success: true;
  data: {
    session: EvalSessionResponse;
    /** Present only when Rekor reported ledger-capacity pressure on acquire. */
    seed_lease_warnings?: SeedLeaseWarning[];
  };
  message: string;
}

/**
 * What the stop call did with the session's seed lease (gh #3745). Every value
 * describes the state at RESPONSE time — none of them claims the fixture is
 * already free, because the release itself completes in the background:
 *   • `not_applicable` — the session held no lease.
 *   • `pending`        — turns admitted before the stop are still running; the
 *                        lease is released by the watchdog once they settle.
 *                        Also reported when the backend could not determine the
 *                        state, since "no lease" would be the unsafe guess.
 *   • `releasing`      — the session was quiescent and the release was
 *                        dispatched; it finishes shortly after this response.
 */
export const seedReleaseStatusSchema = z.enum(['not_applicable', 'pending', 'releasing']);
export type SeedReleaseStatus = z.infer<typeof seedReleaseStatusSchema>;

export interface StopSessionResponse {
  success: true;
  data: {
    session: EvalSessionResponse;
    /**
     * False when the session had already finished and this call changed
     * nothing — the signal callers need to avoid reporting a cancellation that
     * did not happen, and the backend needs to skip fixture teardown.
     */
    stopped: boolean;
    seed_release_status: SeedReleaseStatus;
  };
  message: string;
}

/**
 * A delete refused because the session still holds a seed lease and has not
 * finished draining a turn admitted before it was stopped (gh #3745). 409.
 * Deleting would destroy the row carrying the lease tuple, so the lease could
 * never be released; releasing first would clear the fixture under a live writer.
 * Retrying once the drain completes succeeds.
 */
/** The error code both delete paths return on a drain conflict. */
export const EVAL_SESSION_NOT_QUIESCENT = 'session_not_quiescent';

export interface EvalSessionNotQuiescentResponse {
  success: false;
  error: typeof EVAL_SESSION_NOT_QUIESCENT;
  message: string;
}

export interface DeleteSessionResponse {
  success: true;
  message: string;
  /** Number of eval conversations torn down alongside the session. */
  data?: { deleted_conversations: number };
}

export interface DeleteAllSessionsResponse {
  success: true;
  message: string;
  data: {
    /** Number of eval sessions deleted. */
    deleted_sessions: number;
    /** Number of eval conversations torn down across all deleted sessions. */
    deleted_conversations: number;
  };
}

export interface GetSessionDetailsResponse {
  success: true;
  data: {
    session: EvalSessionResponse;
    results: EvalSessionResultResponse[];
  };
}

export interface GetSessionResultsResponse {
  success: true;
  data: {
    results: EvalSessionResultResponse[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface GetSessionRunsResponse {
  success: true;
  data: {
    runs: EvalSessionRunResponse[];
    total_count: number;
    session_id: string;
    pagination?: EvalPaginationResponse;
  };
}

/** Per-session resource cost — sums tokens and billable operations over the
 *  unified `conversation` table where `is_eval = true AND eval_session_id = ?`.
 *  `billable_operations_total` matches the OrgDO billing increment.
 *  `cold` indicates ClickHouse cold-start; on cold, all numeric fields fall back to 0
 *  and the client may retry. */
export interface EvalSessionCost {
  conversations_count: number;
  tokens_total: number;
  tokens_input: number;
  tokens_output: number;
  billable_operations_total: number;
}

export interface GetEvalSessionCostResponse {
  success: true;
  data: {
    cost: EvalSessionCost;
    cold?: true;
  };
}

export interface GetEvalAnalyticsResponse {
  success: true;
  data: {
    analytics: EvalAnalyticsData;
  };
}

export interface ExportSessionResultsResponse {
  success: true;
  data: {
    download_url: string;
    filename: string;
    format: string;
  };
  message: string;
}

export interface ValidateEvalResponse {
  success: true;
  data: {
    is_valid: boolean;
    validation_errors: string[];
    suggestions?: string[];
  };
}

export interface GetHubAgentsResponse {
  success: true;
  data: {
    agents: EvalAgentReference[];
  };
}

export interface GetHubsListResponse {
  success: true;
  data: {
    hubs: EvalHubReference[];
  };
}

// -- Scenario set wrappers --

export interface GetScenarioSetsResponse {
  success: true;
  data: {
    scenario_sets: ScenarioSetResponse[];
    total_count: number;
  };
}

export interface GetScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
}

export interface CreateScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
  message: string;
}

export interface UpdateScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
  message: string;
}

export interface DeleteScenarioSetResponse {
  success: true;
  message: string;
}

export interface DeleteScenarioSetConflictResponse {
  success: false;
  error: 'scenario_set_not_empty';
  scenario_count: number;
  message: string;
}

export interface ListScenariosBySetResponse {
  success: true;
  data: {
    scenarios: EvalResponse[];
    total_count: number;
  };
}

// -- Journey wrappers --

export interface GetJourneysResponse {
  success: true;
  data: { journeys: JourneyResponse[]; total_count: number };
}

export interface GetJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
}

export interface CreateJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

export interface UpdateJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

export interface DeleteJourneyResponse {
  success: true;
  message: string;
}

export interface DeleteJourneyErrorResponse {
  success: false;
  error: 'not_found';
  message: string;
}

/** A journey run creates + runs a session targeting the journey's managed set. */
export interface RunJourneyResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

// -- Compare sessions --

/** Per-scenario stats for a single session in a comparison. */
export interface CompareScenarioStats {
  total_runs: number;
  success_rate: number;
  avg_execution_time_ms: number;
  /** Per-dimension averages from `data.eval_scores.*` (excludes meta dimensions). */
  score_dimensions: Record<string, number>;
}

/** Aggregate session-level summary for the compare summary bar. */
export interface CompareSessionSummary {
  eval_session_id: string;
  session_name: string;
  scenario_set_id: string | null;
  scenario_set_name: string | null;
  agent: AgentConfigSnapshot | null;
  total_runs: number;
  success_rate: number;
  avg_execution_time_ms: number;
  tokens_total: number;
  billable_operations_total: number;
}

/** Per-scenario row in the diff table — only scenarios present in BOTH sessions. */
export interface CompareScenarioRow {
  eval_id: string;
  scenario_name: string;
  a: CompareScenarioStats;
  b: CompareScenarioStats;
  /** `b.success_rate - a.success_rate` (signed delta). */
  delta_success_rate: number;
}

export interface CompareSessionsResponse {
  success: true;
  data: {
    session_a: CompareSessionSummary;
    session_b: CompareSessionSummary;
    per_scenario: CompareScenarioRow[];
    /** True when session A and B targeted different scenario sets — UI shows a warning. */
    scenario_sets_match: boolean;
    /** `cold` flags ClickHouse cold-start; numeric fields fall back to zeros and the client may retry. */
    cold?: true;
  };
}

// -- Production replay (create scenario from conversation) --

export interface CreateScenarioFromConversationResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

// -- Production replay (capture conversation as journey) --

export interface CreateJourneyFromConversationResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

// -- Read-only SQL (CLI agent surface) --

export interface EvalsSqlResponse {
  success: true;
  data: Record<string, unknown>[];
  statistics?: {
    elapsed: number;
    rows_read: number;
    bytes_read: number;
  };
  cold?: boolean;
}

export interface EvalsSqlSchemaPath {
  path: string;
  type: 'numeric' | 'categorical' | 'text' | 'unknown';
  origin: 'system' | 'eval_scores';
  category?: string;
  description?: string;
}

export interface EvalsSqlSchemaResponse {
  success: true;
  data: {
    table: 'conversation';
    top_level: Array<{ column: string; type: string }>;
    paths: EvalsSqlSchemaPath[];
  };
}
